<template>
  <main class="search-page iso-container">
    <h1 class="iso-section-title">{{ $t('search.title') }}</h1>

    <div class="search-box">
      <input
        v-model="keyword"
        type="text"
        class="iso-input search-input"
        :placeholder="$t('search.placeholder')"
        @input="onSearch"
      />
      <button class="iso-btn search-btn" @click="doSearch">{{ $t('search.title') }}</button>
    </div>

    <div v-if="searched" class="search-results">
      <p class="results-hint">{{ $t('search.resultCount', { count: articles.length }) }}</p>
      <div v-if="articles.length" class="iso-grid">
        <IsometricCard
          v-for="article in articles"
          :key="article.id"
          :id="article.id"
          :title="article.title"
          :summary="article.summary"
          :cover-image="article.cover_image"
          :date="article.published_at"
          :category="article.category"
          :slug="article.slug"
        />
      </div>
      <div v-else class="empty-state">
        <p>{{ $t('search.noResult') }}</p>
      </div>
    </div>
    <div v-if="loading" class="iso-loading">{{ $t('common.loading') }}</div>
  </main>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { request } from '@/api'
import { debounce } from '@/utils'
import IsometricCard from '@/components/IsometricCard.vue'

interface Article {
  id: number
  title: string
  slug: string
  summary?: string
  category?: string
  cover_image?: string
  published_at?: string
}

const keyword = ref('')
const articles = ref<Article[]>([])
const loading = ref(false)
const searched = ref(false)

async function doSearch() {
  const q = keyword.value.trim()
  if (!q) return
  loading.value = true
  searched.value = true
  try {
    const res = await request<{ data: Article[] }>(`/news?limit=20&search=${encodeURIComponent(q)}`)
    articles.value = res.data || []
  } catch {
    articles.value = []
  } finally {
    loading.value = false
  }
}

const onSearch = debounce(() => {
  doSearch()
}, 400)
</script>

<style scoped>
.search-page {
  padding-top: calc(var(--header-height) + 32px);
  padding-bottom: 48px;
  max-width: 860px;
}

.search-box {
  display: flex;
  gap: 12px;
  margin-bottom: 32px;
}

.search-input {
  flex: 1;
}

.search-btn {
  flex-shrink: 0;
}

.results-hint {
  font-size: 13px;
  color: var(--iso-text-dim);
  margin-bottom: 20px;
}

.empty-state {
  text-align: center;
  padding: 64px 24px;
  color: var(--iso-text-dim);
}

@media (max-width: 768px) {
  .search-box {
    flex-direction: column;
  }
}
</style>
